import React from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useClickOutside } from '../hooks/useClickOutside';

interface StockErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  message: string;
  productName?: string;
  availableStock?: number;
}

const StockErrorModal: React.FC<StockErrorModalProps> = ({
  isOpen,
  onClose,
  message,
  productName,
  availableStock
}) => {
  const modalRef = useClickOutside(onClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300" />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          ref={modalRef}
          className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 transform transition-all duration-300 ease-out"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {/* Icon */}
          <div className="flex items-center justify-center w-12 h-12 bg-amber-100 rounded-full mx-auto mb-4">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
          </div>

          {/* Content */}
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Stock Limit Reached
            </h3>
            
            {productName && (
              <p className="text-sm text-gray-600 mb-3">
                {productName}
              </p>
            )}
            
            <p className="text-gray-700 mb-4">
              {message}
            </p>
            
            {availableStock !== undefined && (
              <div className="bg-gray-50 rounded-lg p-3 mb-6">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Available Stock:</span> {availableStock} items
                </p>
              </div>
            )}

            {/* Action Button */}
            <button
              onClick={onClose}
              className="w-full bg-gray-900 text-white py-3 px-4 rounded-lg hover:bg-gray-800 transition-all duration-200 ease-in-out hover:shadow-md active:scale-[0.998] font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockErrorModal;
